import { inject } from "@angular/core";
import { ResolveFn } from "@angular/router";
import {
  catchError,
  EMPTY,
  first,
  firstValueFrom,
  Observable,
  of,
  startWith,
  switchMap,
  take,
  takeUntil,
  timeout,
  timer,
} from "rxjs";
import { LoadingStateService } from "src/app/services/loading-state/loading-state.service";
import { AfpService } from "src/app/services/prognoses-services/afp.service";
import { BankService } from "../services/prognoses-services/bank.service";
import { NavService } from "../services/prognoses-services/nav.service";
import { NorskpensjonRequirementsService } from "../services/prognoses-services/norskpensjon-requirements.service";
import { NorskpensjonService } from "../services/prognoses-services/norskpensjon.service";
import { PublicPensionService } from "../services/prognoses-services/public-pension.service";
import { SavingsAndPensionService } from "../services/prognoses-services/savings-and-pension.service";
import { UnavailableNorskpensjonService } from "../services/prognoses-services/unavailable-norskpensjon.service";

const RESOLVER_TIMEOUT = 30000;

/**
 * Runs a prognosis prefetch as its own fire-and-forget subscription, so the engagement fetch can't cut it off.
 * Bounded by a fixed window rather than `timeout`, since the prefetch emits once per query and may never complete.
 */
function runPrefetch(prefetch$: Observable<unknown>): void {
  prefetch$
    .pipe(
      catchError(() => EMPTY),
      takeUntil(timer(RESOLVER_TIMEOUT)),
    )
    .subscribe();
}

export const afpResolver: ResolveFn<boolean> = () => {
  const loadingStateService = inject(LoadingStateService);
  const norskpensjonRequirementsService = inject(NorskpensjonRequirementsService);
  const afpService = inject(AfpService);

  const fetchEngagements$ = afpService.fetchEngagements().pipe(loadingStateService.withTracking("AfpResolver"));

  norskpensjonRequirementsService
    .getHasAllRequirements()
    .pipe(
      first(Boolean),
      startWith(null),
      switchMap(() => fetchEngagements$),
      take(1),
      timeout(RESOLVER_TIMEOUT),
      catchError(() => of([])),
    )
    .subscribe();

  return true;
};

export const bankResolver: ResolveFn<boolean> = () => {
  const loadingStateService = inject(LoadingStateService);
  const bankService = inject(BankService);

  bankService
    .fetchEngagements()
    .pipe(
      loadingStateService.withTracking("BankResolver"),
      take(1),
      timeout(RESOLVER_TIMEOUT),
      catchError(() => of([])),
    )
    .subscribe();
  return true;
};

export const navResolver: ResolveFn<boolean> = () => {
  const navService = inject(NavService);
  const norskpensjonRequirementsService = inject(NorskpensjonRequirementsService);
  const loadingStateService = inject(LoadingStateService);

  const fetchEngagements$ = navService.fetchEngagements().pipe(loadingStateService.withTracking("NavResolver"));

  const prefetchPrognosesInRange$ = navService.prefetchPrognosesInRange();

  const hasAllRequirementsTrigger$ = norskpensjonRequirementsService
    .getHasAllRequirements()
    .pipe(first(Boolean), startWith(null));

  hasAllRequirementsTrigger$
    .pipe(
      switchMap(() => fetchEngagements$),
      take(1),
      timeout(RESOLVER_TIMEOUT),
      catchError(() => of([])),
    )
    .subscribe();

  runPrefetch(hasAllRequirementsTrigger$.pipe(switchMap(() => prefetchPrognosesInRange$)));

  return true;
};

export const norskpensjonResolver: ResolveFn<boolean> = () => {
  const norskpensjonService = inject(NorskpensjonService);
  const loadingStateService = inject(LoadingStateService);

  const fetchEngagements$ = norskpensjonService
    .fetchEngagements()
    .pipe(loadingStateService.withTracking("NorskpensjonResolver"));
  const prefetchPrognosesInRange$ = norskpensjonService.prefetchPrognosesInRange();

  fetchEngagements$
    .pipe(
      take(1),
      timeout(RESOLVER_TIMEOUT),
      catchError(() => of([])),
    )
    .subscribe();

  runPrefetch(prefetchPrognosesInRange$);

  return true;
};

export const unavailableNorskpensjonResolver: ResolveFn<boolean> = () => {
  const unavailableNorskpensjonService = inject(UnavailableNorskpensjonService);
  const loadingStateService = inject(LoadingStateService);

  unavailableNorskpensjonService
    .fetchEngagements()
    .pipe(
      loadingStateService.withTracking("UnavailableNorskpensjonResolver"),
      take(1),
      timeout(RESOLVER_TIMEOUT),
      catchError(() => of([])),
    )
    .subscribe();

  return true;
};

export const publicPensionResolver: ResolveFn<boolean> = () => {
  const publicPensionService = inject(PublicPensionService);
  const loadingStateService = inject(LoadingStateService);

  firstValueFrom(
    publicPensionService.fetchEngagements().pipe(
      loadingStateService.withTracking("PublicPensionResolver"),
      timeout(RESOLVER_TIMEOUT),
      catchError(() => of([])),
    ),
  );

  return true;
};

export const savingsAndPensionResolver: ResolveFn<boolean> = () => {
  const savingsAndPensionService = inject(SavingsAndPensionService);
  const loadingStateService = inject(LoadingStateService);

  savingsAndPensionService
    .fetchEngagements()
    .pipe(
      loadingStateService.withTracking("SavingsAndPensionResolver"),
      take(1),
      timeout(RESOLVER_TIMEOUT),
      catchError(() => of([])),
    )
    .subscribe();

  return true;
};
